<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="440" persistent>
    <v-card rounded="xl">
      <v-card-title class="pa-6 pb-2">
        <div class="d-flex align-center justify-space-between">
          <div>
            <h2 class="dialog-title">Link WhatsApp</h2>
            <p class="text-body-2 text-medium-emphasis mt-1">{{ agent?.name }}</p>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="cancel" />
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6 text-center">

        <!-- Idle — not started yet -->
        <div v-if="state === 'idle'">
          <v-icon size="64" color="#667de8" class="mb-4">mdi-whatsapp</v-icon>
          <p class="text-body-2 text-medium-emphasis mb-6">
            Scan a QR code with the WhatsApp account you want to link to this agent.
            Keep your phone nearby.
          </p>
          <v-btn color="#667de8" variant="flat" rounded="lg" prepend-icon="mdi-qrcode-scan" @click="start">
            Generate QR Code
          </v-btn>
        </div>

        <!-- Loading — waiting for QR -->
        <div v-else-if="state === 'loading'" class="py-4">
          <v-progress-circular indeterminate color="#667de8" size="48" class="mb-4" />
          <p class="text-body-2 text-medium-emphasis">{{ statusMessage || 'Connecting to OpenClaw…' }}</p>
        </div>

        <!-- QR ready to scan -->
        <div v-else-if="state === 'qr'">
          <p class="text-body-2 text-medium-emphasis mb-4">
            Open WhatsApp → <strong>Linked Devices</strong> → scan this code
          </p>
          <div class="qr-ascii-wrapper mx-auto mb-4">
            <pre class="qr-ascii">{{ qrAscii }}</pre>
          </div>
          <v-chip color="orange" variant="tonal" size="small" prepend-icon="mdi-timer-outline">
            QR expires in ~60 seconds
          </v-chip>
          <p v-if="statusMessage" class="text-caption text-medium-emphasis mt-3">{{ statusMessage }}</p>
        </div>

        <!-- Paired! -->
        <div v-else-if="state === 'paired'" class="py-4">
          <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
          <h3 class="font-weight-bold mb-2" style="color:#1a1a2e">WhatsApp Linked!</h3>
          <p class="text-body-2 text-medium-emphasis">
            {{ agent?.name }} is now connected to WhatsApp.
          </p>
        </div>

        <!-- Error -->
        <div v-else-if="state === 'error'" class="py-4">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
          <h3 class="font-weight-bold mb-2" style="color:#1a1a2e">Pairing failed</h3>
          <p class="text-body-2 text-medium-emphasis mb-4">{{ errorMessage }}</p>
          <v-btn variant="tonal" color="#667de8" rounded="lg" @click="reset">Try Again</v-btn>
        </div>

      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn v-if="state !== 'paired'" variant="text" rounded="lg" @click="cancel">
          Cancel
        </v-btn>
        <v-btn v-if="state === 'paired'" color="#667de8" variant="flat" rounded="lg" @click="done">
          Done
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from 'vue'

export default {
  name: 'WaPairingDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    agent: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'paired'],
  setup(props, { emit }) {
    const state = ref('idle')   // idle | loading | qr | paired | error
    const pairing = ref(false)
    const statusMessage = ref('')
    const errorMessage = ref('')
    const qrAscii = ref('')
    let evtSource = null

    const token = () => localStorage.getItem('token')

    function reset() {
      state.value = 'idle'
      pairing.value = false
      statusMessage.value = ''
      errorMessage.value = ''
      qrAscii.value = ''
      if (evtSource) { evtSource.close(); evtSource = null }
    }

    function cancel() {
      reset()
      emit('update:modelValue', false)
    }

    function done() {
      emit('paired', props.agent)
      emit('update:modelValue', false)
      reset()
    }

    async function start() {
      if (!props.agent) return
      reset()
      state.value = 'loading'
      pairing.value = true

      // EventSource doesn't support Authorization headers natively.
      // We pass the token as a query param. The backend should accept it.
      const apiBase = process.env.VUE_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:3000'
      const url = `${apiBase}/api/agents/${props.agent.id}/pair?token=${encodeURIComponent(token())}`
      evtSource = new EventSource(url)

      evtSource.addEventListener('qr', e => {
        let { qr } = JSON.parse(e.data)
        // If server sent a raw JSON log entry instead of extracted ASCII art, unwrap it
        if (qr && qr.startsWith('{')) {
          try {
            const logEntry = JSON.parse(qr)
            if (typeof logEntry['0'] === 'string') qr = logEntry['0']
          } catch {
            const m = qr.match(/"0":"([\s\S]+?)","_meta":/)
            if (m) qr = m[1].replace(/\\n/g, '\n').replace(/\\"/g, '"')
          }
        }
        qrAscii.value = qr
        state.value = 'qr'
      })

      evtSource.addEventListener('paired', () => {
        state.value = 'paired'
        pairing.value = false
        evtSource.close()
        evtSource = null
      })

      evtSource.addEventListener('status', e => {
        const { message } = JSON.parse(e.data)
        statusMessage.value = message
      })

      evtSource.addEventListener('error', e => {
        const msg = e.data ? JSON.parse(e.data).message : 'Connection lost'
        state.value = 'error'
        errorMessage.value = msg
        pairing.value = false
        evtSource.close()
        evtSource = null
      })

      evtSource.addEventListener('close', () => {
        // Don't interrupt if QR is being displayed — user may still be scanning
        if (state.value !== 'paired' && state.value !== 'qr') {
          state.value = 'error'
          errorMessage.value = 'Session closed. Try again.'
        }
        pairing.value = false
        evtSource?.close()
        evtSource = null
      })

      evtSource.onerror = () => {
        if (state.value !== 'paired' && state.value !== 'qr') {
          state.value = 'error'
          errorMessage.value = 'Connection to server lost. Check the server is running.'
        }
        pairing.value = false
        evtSource?.close()
        evtSource = null
      }
    }

    // Reset state when dialog closes externally
    watch(() => props.modelValue, open => {
      if (!open) reset()
    })

    return { state, pairing, statusMessage, errorMessage, qrAscii, start, cancel, done, reset }
  }
}
</script>

<style scoped>
.dialog-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a2e;
}
.qr-ascii-wrapper {
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 8px;
  background: #fff;
  display: inline-block;
}
.qr-ascii {
  font-family: 'Courier New', monospace;
  font-size: 4px;
  line-height: 1;
  letter-spacing: 0;
  margin: 0;
  color: #1a1a2e;
  white-space: pre;
  display: block;
}
</style>
